// Shop drawings client.
//
// Mounted at /projects/:projectId/shop-drawings. THE DELIVERY IS THE
// IMPORT: submit posts the FILE, and the server files it in Document
// Control on the same request - there is no separate upload step and no
// state where a delivered sheet exists outside Document Control.
import { url, tokenRef } from "api";

const authHeader = () => {
  const userToken = localStorage.getItem(tokenRef);
  return userToken ? { Authorization: `Bearer ${userToken}` } : {};
};
const jsonHeaders = () => ({ "Content-Type": "application/json", ...authHeader() });

const handle = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.err || "Request failed");
    error.status = response.status;
    throw error;
  }
  return data;
};

const base = (projectId) => `${url}/projects/${projectId}/shop-drawings`;

export const getShopDrawings = (projectId, params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, String(v)); });
  const s = q.toString();
  return fetch(`${base(projectId)}${s ? `?${s}` : ""}`, { headers: jsonHeaders() }).then(handle);
};

// WHAT THE ADD FORM PICKS FROM, in one call: this project's
// shop-drawing schedule steps (each carrying the area and package the
// form autofills from it), the packages the schedule actually uses, and
// the same level/area/grid options the issue location picker reads.
export const getShopDrawingOptions = (projectId, params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, String(v)); });
  const s = q.toString();
  return fetch(`${base(projectId)}/options${s ? `?${s}` : ""}`, { headers: jsonHeaders() }).then(handle);
};

export const addShopDrawing = (projectId, body) =>
  fetch(base(projectId), { method: "POST", headers: jsonHeaders(), body: JSON.stringify(body) }).then(handle);

// WHO GETS THE APPROVED DELIVERABLE - a different audience from the
// responsible partners, and a separate call for the same reason the route
// is separate: naming somebody here sends a project document to an address
// a person typed. The answer carries `rejected`, so the dialog can say
// which addresses it refused instead of silently keeping the good ones.
export const setShopDrawingRecipients = (projectId, id, recipients) =>
  fetch(`${base(projectId)}/${id}/recipients`, {
    method: "PUT", headers: jsonHeaders(), body: JSON.stringify({ recipients }),
  }).then(handle);

// EDIT - one row, and many at once (owner, 2026-09-30).
//
// The FIELDS are the server's allowlist (utils/shopDrawingEdit), not a list
// repeated here: sending something it does not accept comes back in `rejected`
// rather than being dropped, so the caller can say so instead of the person
// wondering why nothing happened.
export const editShopDrawing = (projectId, id, patch) =>
  fetch(`${base(projectId)}/${id}`, {
    method: "PUT", headers: jsonHeaders(), body: JSON.stringify(patch || {}),
  }).then(handle);

// ONE set of values over many ids. The server caps it and refuses past the cap
// rather than truncating, so a refusal here is a real answer and not a partial
// write that looked like success.
// THE RECIPIENT LIST ON MANY DRAWINGS (owner, 2026-10-05). A SIBLING of
// bulkEditShopDrawings, not a field inside it: recipients is deliberately
// absent from the server's edit allowlist so normaliseRecipients cannot be
// bypassed, and issues answer the same shape - a plain bulk edit beside
// separate validated routes for the lists that decide who gets mail.
export const bulkSetShopDrawingRecipients = (projectId, ids, recipients) =>
  fetch(`${base(projectId)}/bulk-recipients`, {
    method: "PUT", headers: jsonHeaders(), body: JSON.stringify({ ids: ids || [], recipients: recipients || [] }),
  }).then(handle);

export const bulkEditShopDrawings = (projectId, ids, set) =>
  fetch(`${base(projectId)}/bulk`, {
    method: "PUT", headers: jsonHeaders(), body: JSON.stringify({ ids: ids || [], set: set || {} }),
  }).then(handle);

// DELETE, one or many (owner, 2026-10-14): super admin, admin and company
// admin only - the server refuses anyone else and the list's canDelete says
// whether to show the buttons at all.
export const deleteShopDrawing = (projectId, id) =>
  fetch(`${base(projectId)}/${id}`, { method: "DELETE", headers: jsonHeaders() }).then(handle);
export const bulkDeleteShopDrawings = (projectId, ids) =>
  fetch(`${base(projectId)}/bulk-delete`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ ids: ids || [] }),
  }).then(handle);

// GAP 5 - the field says it had it. Not owner-only on the server, because the
// person recording receipt is the person who received it. Acknowledging twice
// keeps the first date, and the reply says which happened.
export const acknowledgeShopDrawing = (projectId, id, note) =>
  fetch(`${base(projectId)}/${id}/acknowledge`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ note: note || "" }),
  }).then(handle);

export const undoAcknowledge = (projectId, id) =>
  fetch(`${base(projectId)}/${id}/acknowledge`, { method: "DELETE", headers: jsonHeaders() }).then(handle);

// GAP 3 - who gave direction, the outcome, the date. Recorded beside the status,
// never as one of them: STATUSES drives the review gate and the schedule
// sign-off.
export const recordDirection = (projectId, id, outcome, at) =>
  fetch(`${base(projectId)}/${id}/direction`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ outcome, at }),
  }).then(handle);

// GAP 6 - Kaleb's list. Delivered AND signed off by default; only=all for the
// whole register. params.format: csv (default) | xlsx | pdf (2026-10-20). The COUNT comes back in a header, so a file of nothing can
// still be reported as nothing rather than silently saved.
export const downloadRegisterCsv = async (projectId, params = {}) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v));
  const res = await fetch(`${base(projectId)}/register${qs.toString() ? `?${qs}` : ""}`, { headers: authHeader() });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || "Could not build the register");
  }
  const count = Number(res.headers.get("X-Register-Count") || 0);
  const note = res.headers.get("X-Register-Note") || "";
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = href;
  a.download = (((res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1]) || `register.${params.format || "csv"}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
  return { count, note };
};

// The same rows as text, for pasting into an email.
export const getRegisterText = (projectId, params = {}) => {
  const qs = new URLSearchParams({ ...params, format: "text" });
  return fetch(`${base(projectId)}/register?${qs}`, { headers: authHeader() }).then(handle);
};

export const setInternalRelease = (projectId, id, released) =>
  fetch(`${base(projectId)}/${id}/internal-release`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ released }),
  }).then(handle);

// The delivery: multipart, because the file IS the submission.
// BULK SHEET IMPORT - two calls, and the first writes nothing.
//
// proposeSheets reads the project and says what the drop WOULD become;
// applySheets writes the rows the person corrected and hands back the drawing
// id each file belongs to. The FILES then go through submitShopDrawing, which
// already hashes, files into Document Control and bumps the revision - the
// import deliberately does not re-implement a delivery.
export const proposeSheets = (projectId, files) =>
  fetch(`${base(projectId)}/sheets/propose`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ files }),
  }).then(handle);

export const applySheets = (projectId, rows) =>
  fetch(`${base(projectId)}/sheets/apply`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ rows }),
  }).then(handle);

export const submitShopDrawing = (projectId, id, { file, hoursSpent }) => {
  const form = new FormData();
  if (file) form.append("file", file);
  form.append("hoursSpent", String(hoursSpent));
  return fetch(`${base(projectId)}/${id}/submit`, {
    method: "POST", headers: authHeader(), body: form,
  }).then(handle);
};

// THE PROJECT'S DEFAULT RECIPIENT LIST (owner, 2026-10-05), applied AT
// CREATION ONLY: a drawing copies it when it is made and owns its copy for
// ever, so changing this touches nothing that already exists. The server
// says that in `appliesTo` and the dialog prints it, rather than leaving
// somebody to discover it by changing the default and watching nothing
// happen to the register.
export const getShopDrawingRecipientDefaults = (projectId) =>
  fetch(`${base(projectId)}/recipient-defaults`, { headers: jsonHeaders() }).then(handle);

export const setShopDrawingRecipientDefaults = (projectId, recipients) =>
  fetch(`${base(projectId)}/recipient-defaults`, {
    method: "PUT", headers: jsonHeaders(), body: JSON.stringify({ recipients }),
  }).then(handle);

// RE-ISSUE: ask for a drawing to be issued again (owner, 2026-10-05).
//
// A SIBLING ROUTE, not a field on the edit: the ask mails the responsibles
// and re-opens the row, which is a consequence an edit does not have. The
// bulk form is `/reissue/bulk` and is registered BEFORE `/:id` on the
// server, so "reissue" is never read as a drawing id.
// SEND the approved deliverable now, or RESEND it once delivered. Answers
// with how many were mailed and, if it did not all go, why.
export const sendShopDrawing = (projectId, id) =>
  fetch(`${base(projectId)}/${id}/send`, { method: "POST", headers: jsonHeaders() }).then(handle);

export const requestReissue = (projectId, id, { reason, dueAt } = {}) =>
  fetch(`${base(projectId)}/${id}/reissue`, {
    method: "POST", headers: jsonHeaders(),
    body: JSON.stringify({ reason, ...(dueAt ? { dueAt } : {}) }),
  }).then(handle);

export const bulkRequestReissue = (projectId, ids, { reason, dueAt } = {}) =>
  fetch(`${base(projectId)}/reissue/bulk`, {
    method: "POST", headers: jsonHeaders(),
    body: JSON.stringify({ ids, reason, ...(dueAt ? { dueAt } : {}) }),
  }).then(handle);

// signOffArea: the reviewer's assertion that no further sheet is coming, so
// validating this one also signs the area off. Omitted or false, the drawing is
// validated and the schedule activity is left alone (owner, 2026-10-08:
// "signing off an area should not be determined by 1 sheet/drawing shop").
export const decideShopDrawing = (projectId, id, { approve, note, force, signOffArea }) =>
  fetch(`${base(projectId)}/${id}/decide`, {
    method: "POST", headers: jsonHeaders(),
    body: JSON.stringify({ approve, note, force, signOffArea }),
  }).then(handle);

export const removeShopDrawing = (projectId, id) =>
  fetch(`${base(projectId)}/${id}`, { method: "DELETE", headers: jsonHeaders() }).then(handle);

// THE REGISTER AS PAPER. A GET rather than a POST because it carries no
// body - the filters travel in the query, and the server scopes it the
// same way it scopes the table, so a partner's paper cannot show more
// than their screen did.
//
// The server names the file (the project's own prefix, "SPX-…"); ours is
// only the fallback for a response that arrives without the header.
// The register PDF as a blob, scoped and filtered by the server exactly as
// the table is. Export downloads it; Print hands the same blob to the print
// dialog, so the two cannot read differently.
// The table export in any of its three forms (owner 2026-10-19: "pdf,
// excel, csv"). Same scope and filters for all three - the server builds them
// from one row set.
export const fetchShopDrawingsExport = async (projectId, ext, { status = "", area = "", filters = null, includeHours, includeEstimates } = {}) => {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (area) qs.set("area", area);
  // THE TOOLBAR'S WHOLE FILTER (2026-10-15): with `filters`, the register
  // covers exactly the rows on screen - name, activity, level, area sign-off.
  if (filters) {
    qs.set("filters", "1");
    for (const [k, v] of Object.entries(filters)) {
      if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) continue;
      qs.set(k, Array.isArray(v) ? v.join(",") : String(v));
    }
  }
  // the popup's answer, sent only when it was asked
  if (includeHours !== undefined) qs.set("includeHours", includeHours ? "true" : "false");
  if (includeEstimates !== undefined) qs.set("includeEstimates", includeEstimates ? "true" : "false");
  const res = await fetch(`${base(projectId)}/export.${ext}${qs.toString() ? `?${qs}` : ""}`, {
    headers: authHeader(),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || "Could not build the export");
  }
  const filename = (((res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1])
    || `shop-drawings.${ext}`;
  const count = res.headers.get("X-Export-Count");
  return { blob: await res.blob(), filename, count: count === null ? null : Number(count) };
};

export const fetchShopDrawingsPdf = (projectId, opts = {}) => fetchShopDrawingsExport(projectId, "pdf", opts);

// What the export popup may offer on this project (owner, 2026-10-22 - the
// same question and the same shape as the issue manager's).
export const getShopDrawingExportOptions = async (projectId) => {
  const res = await fetch(`${base(projectId)}/export/options`, { headers: authHeader() });
  if (!res.ok) return { hours: false, prices: false };
  return res.json();
};

export const exportShopDrawings = async (projectId, ext, opts = {}) => {
  const { blob, filename, count } = await fetchShopDrawingsExport(projectId, ext, opts);
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
  return { count };
};

export const exportShopDrawingsPdf = async (projectId, opts = {}) => {
  const { blob, filename } = await fetchShopDrawingsPdf(projectId, opts);
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick, not immediately: Safari has not finished
  // reading the blob when click() returns.
  setTimeout(() => URL.revokeObjectURL(href), 0);
  return true;
};

// THE TICKED DRAWINGS AS FILES (owner, 2026-10-21): mode 'separate' = a zip of
// the register PDF + one file per drawing named as its row; 'combined' = one
// PDF, the register first. Saves the file and answers what was left out.
export const downloadShopDrawingBundle = async (projectId, ids, mode) => {
  const res = await fetch(`${base(projectId)}/bundle`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify({ ids, mode }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.err || "Could not build the download");
  }
  const filename = (((res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1])
    || `shop-drawings.${mode === "combined" ? "pdf" : "zip"}`;
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
  const n = (h) => Number(res.headers.get(h) || 0);
  let names = "";
  try { names = decodeURIComponent(res.headers.get("X-Bundle-Missing-Names") || ""); } catch (e) { names = ""; }
  return { count: n("X-Bundle-Count"), included: n("X-Bundle-Included"), missing: n("X-Bundle-Missing"), missingNames: names };
};

// SEND TO PROCORE / ACC (owner, 2026-10-23). Which platforms can take shop
// drawings right now, and why not; then queue the chosen drawings to them.
export const getShopDrawingExternalTargets = (projectId) =>
  fetch(`${base(projectId)}/external-targets`, { headers: jsonHeaders() }).then(handle);

// `destinations`: destination ids (several per platform); `providers`: each platform's first
export const sendShopDrawingsExternal = (projectId, ids, providers, destinations) =>
  fetch(`${base(projectId)}/send-external`, {
    method: "POST", headers: jsonHeaders(), body: JSON.stringify(destinations ? { ids, providers, destinations } : { ids, providers }),
  }).then(handle);
